<template>
  <v-card outlined>
    <v-card-title class="d-flex align-center flex-wrap" style="gap: 8px;">
      <v-icon class="mr-1">mdi-api</v-icon>
      {{ t('admConfig.tools.title') }}
      <v-chip v-if="hasChanges" color="warning" size="small" label>{{ t('admConfig.tools.unsaved') }}</v-chip>
      <v-spacer />
      <v-btn variant="text" prepend-icon="mdi-undo" :disabled="!hasChanges || saving" @click="reset">
        {{ t('admConfig.statusEditor.discard') }}
      </v-btn>
      <v-btn color="primary" variant="flat" prepend-icon="mdi-content-save" :loading="saving" :disabled="!hasChanges" @click="save">
        {{ t('admConfig.tools.save') }}
      </v-btn>
    </v-card-title>

    <v-card-text>
      <div class="text-body-2 text-medium-emphasis mb-4">{{ t('admConfig.tools.description') }}</div>

      <v-alert v-if="loadError" type="error" variant="tonal" density="compact" class="mb-3">{{ loadError }}</v-alert>
      <v-alert v-if="saveError" type="error" variant="tonal" density="compact" class="mb-3">
        {{ saveError }}
        <ul v-if="saveErrors.length" class="mt-2 ml-4">
          <li v-for="(e, i) in saveErrors" :key="i">{{ e }}</li>
        </ul>
      </v-alert>
      <v-alert v-if="warnings.length" type="warning" variant="tonal" density="compact" class="mb-3">
        <div class="font-weight-medium">{{ t('admConfig.tools.warnings') }}</div>
        <ul class="ml-4">
          <li v-for="(w, i) in warnings" :key="i">{{ w }}</li>
        </ul>
      </v-alert>
      <v-alert v-if="saveSuccess" type="success" variant="tonal" density="compact" class="mb-3">{{ saveSuccess }}</v-alert>

      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3" />
      <v-alert v-else-if="!catalog.length" type="info" variant="tonal" density="compact">{{ t('admConfig.tools.empty') }}</v-alert>

      <div v-for="tool in catalog" :key="tool.id" class="mb-4">
        <div class="d-flex align-center mb-2" style="gap: 8px;">
          <span class="text-subtitle-1">{{ tool.label }}</span>
          <code class="text-caption">{{ tool.id }}</code>
        </div>

        <v-expansion-panels variant="accordion" multiple>
          <v-expansion-panel v-for="call in tool.calls" :key="call.id">
            <v-expansion-panel-title>
              <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                <v-icon size="small" :color="callCfg(tool.id, call.id).enabled === false ? 'grey' : 'teal'">mdi-swap-horizontal</v-icon>
                <span>{{ call.label }}</span>
                <code class="text-caption">{{ call.id }}</code>
                <v-chip v-if="callCfg(tool.id, call.id).enabled === false" size="x-small" label color="warning" variant="tonal">
                  {{ t('admConfig.tools.disabled') }}
                </v-chip>
              </div>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <v-switch
                v-model="callCfg(tool.id, call.id).enabled"
                :label="callCfg(tool.id, call.id).enabled === false ? t('admConfig.tools.disabled') : t('admConfig.tools.enabled')"
                color="primary" density="compact" hide-details class="mb-3"
              />

              <!-- Input: da quale campo del processo prendere ogni parametro -->
              <div class="text-caption text-medium-emphasis mb-1">{{ t('admConfig.tools.inputs') }}</div>
              <v-row dense class="mb-2">
                <v-col v-for="input in call.inputs" :key="input.key" cols="12" md="4">
                  <v-autocomplete
                    v-model="callCfg(tool.id, call.id).inputs[input.key]"
                    :items="allFieldItems"
                    item-title="title"
                    item-value="value"
                    :label="`${input.label} (${input.key})`"
                    :hint="input.hint || ''"
                    density="compact"
                    persistent-hint
                    clearable
                  />
                </v-col>
              </v-row>

              <!-- Output: in quale campo readonly scrivere ogni valore -->
              <div class="text-caption text-medium-emphasis mb-1">
                {{ t('admConfig.tools.outputs') }} — {{ t('admConfig.tools.outputHint') }}
              </div>
              <v-row dense class="mb-2">
                <v-col v-for="output in call.outputs" :key="output.key" cols="12" md="4">
                  <v-autocomplete
                    v-model="callCfg(tool.id, call.id).outputs[output.key]"
                    :items="fieldItems(f => output.fieldTypes.includes(f.type) && f.readonly)"
                    item-title="title"
                    item-value="value"
                    :label="`${output.label} (${output.key})`"
                    :placeholder="t('admConfig.tools.noField')"
                    density="compact"
                    hide-details
                    clearable
                  />
                </v-col>
              </v-row>

              <!-- Documento prodotto: solo per le chiamate che ne generano uno -->
              <template v-if="call.file">
                <v-divider class="my-3" />
                <div class="text-subtitle-2 mb-1">{{ t('admConfig.tools.file') }}</div>
                <div class="text-caption text-medium-emphasis mb-2">
                  {{ t('admConfig.tools.fileHint', { extension: call.file.extension }) }}
                </div>
                <v-row dense>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="fileCfg(tool.id, call.id).folder"
                      :label="t('admConfig.tools.fileFolder')"
                      :placeholder="call.file.defaultFolder || t('admConfig.tools.fileFolderRoot')"
                      :hint="t('admConfig.tools.fileFolderHint')"
                      density="compact"
                      persistent-hint
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" md="8">
                    <v-text-field
                      v-model="fileCfg(tool.id, call.id).nameTemplate"
                      :label="t('admConfig.tools.fileName')"
                      :placeholder="call.file.defaultNameTemplate"
                      :hint="t('admConfig.tools.fileNameHint', { placeholders: call.file.placeholders.map(p => `{${p}}`).join(' ') })"
                      density="compact"
                      persistent-hint
                      clearable
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="fileCfg(tool.id, call.id).footer"
                      :label="t('admConfig.tools.fileFooter')"
                      :hint="t('admConfig.tools.fileFooterHint')"
                      rows="2"
                      density="compact"
                      persistent-hint
                      clearable
                    />
                  </v-col>
                </v-row>
              </template>

              <!-- Regola di esito -->
              <v-divider class="my-3" />
              <div class="text-subtitle-2 mb-1">{{ t('admConfig.tools.outcome') }}</div>
              <div class="text-caption text-medium-emphasis mb-2">{{ t('admConfig.tools.outcomeHint') }}</div>

              <v-row dense>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="outcomeCfg(tool.id, call.id).source"
                    :items="call.outputs.map(o => ({ title: `${o.label} (${o.key})`, value: o.key, props: { subtitle: o.domain ? `${o.domain.length} valores conhecidos` : '' } }))"
                    item-title="title"
                    item-value="value"
                    :label="t('admConfig.tools.source')"
                    density="compact"
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="outcomeCfg(tool.id, call.id).statusField"
                    :items="fieldItems(f => f.type === 'select' && f.readonly)"
                    item-title="title"
                    item-value="value"
                    :label="t('admConfig.tools.statusField')"
                    density="compact"
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="outcomeCfg(tool.id, call.id).checkboxField"
                    :items="fieldItems(f => f.type === 'checkbox')"
                    item-title="title"
                    item-value="value"
                    :label="t('admConfig.tools.checkboxField')"
                    density="compact"
                    hide-details
                    clearable
                  />
                </v-col>

                <v-col cols="12">
                  <v-combobox
                    v-model="outcomeCfg(tool.id, call.id).successValues"
                    :items="domainOf(call, outcomeCfg(tool.id, call.id).source)"
                    :label="t('admConfig.tools.successValues')"
                    density="compact"
                    hide-details
                    multiple
                    chips
                    closable-chips
                  />
                </v-col>

                <template v-if="outcomeCfg(tool.id, call.id).statusField">
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="outcomeCfg(tool.id, call.id).successCategory"
                      :items="statusOptionItems(outcomeCfg(tool.id, call.id).statusField)"
                      item-title="title"
                      item-value="value"
                      :label="t('admConfig.tools.successCategory')"
                      density="compact" hide-details clearable
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="outcomeCfg(tool.id, call.id).errorCategory"
                      :items="statusOptionItems(outcomeCfg(tool.id, call.id).statusField)"
                      item-title="title"
                      item-value="value"
                      :label="t('admConfig.tools.errorCategory')"
                      density="compact" hide-details clearable
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="outcomeCfg(tool.id, call.id).unknownCategory"
                      :items="statusOptionItems(outcomeCfg(tool.id, call.id).statusField)"
                      item-title="title"
                      item-value="value"
                      :label="t('admConfig.tools.unknownCategory')"
                      density="compact" hide-details clearable
                    />
                  </v-col>

                  <v-col cols="12">
                    <div class="text-caption text-medium-emphasis mt-2 mb-1">
                      {{ t('admConfig.tools.categories') }} — {{ t('admConfig.tools.categoriesHint') }}
                    </div>
                    <v-row dense>
                      <v-col
                        v-for="opt in otherCategories(tool.id, call.id)"
                        :key="opt.value"
                        cols="12" md="6"
                      >
                        <v-combobox
                          :model-value="outcomeCfg(tool.id, call.id).categories[opt.value] || []"
                          @update:model-value="v => setCategory(tool.id, call.id, opt.value, v)"
                          :items="domainOf(call, outcomeCfg(tool.id, call.id).source)"
                          :label="opt.title"
                          density="compact"
                          hide-details
                          multiple
                          chips
                          closable-chips
                        >
                          <template v-slot:prepend-inner>
                            <v-icon v-if="opt.color" size="small" :color="opt.color">mdi-circle</v-icon>
                          </template>
                        </v-combobox>
                      </v-col>
                    </v-row>
                  </v-col>
                </template>
              </v-row>

              <div class="text-caption text-medium-emphasis mt-3">
                <template v-if="actionsUsing(tool.id, call.id).length">
                  {{ t('admConfig.tools.usedBy', { actions: actionsUsing(tool.id, call.id).join(', ') }) }}
                </template>
                <template v-else>{{ t('admConfig.tools.unused') }}</template>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjectStore } from '../stores/projects.js'

const { t } = useI18n()
const projectStore = useProjectStore()

// Catalogo dal codice (GET configs/tools → catalog) e copia di lavoro di tools.json
const catalog = ref([])
const working = ref({})
const originalJson = ref('')

const loading = ref(false)
const loadError = ref('')
const saving = ref(false)
const saveError = ref('')
const saveErrors = ref([])
const saveSuccess = ref('')
const warnings = ref([])

const hasChanges = computed(() => JSON.stringify(working.value) !== originalJson.value)

// ---- campi: tutti i tipi, deduplicati per nome (un tool non appartiene a un tipo solo) ----
const allFields = computed(() => {
  const seen = new Map()
  Object.values(projectStore.fieldsByProjectType || {}).flat().forEach(f => {
    if (f?.name && !seen.has(f.name)) seen.set(f.name, f)
  })
  return [...seen.values()]
})

const toItem = (f) => ({ title: `${f.label || f.name} (${f.name})`, value: f.name })
const allFieldItems = computed(() => allFields.value.map(toItem))
const fieldItems = (predicate) => allFields.value.filter(predicate).map(toItem)

const statusOptionItems = (fieldName) => {
  const field = allFields.value.find(f => f.name === fieldName)
  return (field?.options || []).map(o => ({ title: `${o.text} (${o.value})`, value: String(o.value), color: o.color }))
}

const domainOf = (call, sourceKey) => call.outputs.find(o => o.key === sourceKey)?.domain || []

// ---- configurazione: sempre completa, così i v-model hanno dove scrivere ----
const callCfg = (toolId, callId) => {
  const tool = (working.value[toolId] ||= { calls: {} })
  tool.calls ||= {}
  const call = (tool.calls[callId] ||= {})
  if (call.enabled === undefined) call.enabled = true
  call.inputs ||= {}
  call.outputs ||= {}
  return call
}

const outcomeCfg = (toolId, callId) => {
  const call = callCfg(toolId, callId)
  const o = (call.outcome ||= {})
  o.successValues ||= []
  o.categories ||= {}
  return o
}

const fileCfg = (toolId, callId) => {
  const call = callCfg(toolId, callId)
  return (call.file ||= {})
}

// Opzioni del campo di stato che non sono già successo/errore/non mappato
const otherCategories = (toolId, callId) => {
  const o = outcomeCfg(toolId, callId)
  const taken = new Set([o.successCategory, o.errorCategory, o.unknownCategory].filter(Boolean))
  return statusOptionItems(o.statusField).filter(opt => !taken.has(opt.value))
}

const setCategory = (toolId, callId, optionValue, values) => {
  const o = outcomeCfg(toolId, callId)
  const clean = (values || []).map(v => (v && typeof v === 'object' ? v.value ?? v.title : v)).filter(Boolean)
  if (clean.length) o.categories[optionValue] = clean
  else delete o.categories[optionValue]
}

const actionsUsing = (toolId, callId) =>
  (projectStore.aiActions || [])
    .filter(a => a.engine === 'tool' && a.tool === toolId && a.call === callId)
    .map(a => a.label || a.id)

// ---- caricamento / salvataggio ----
const load = async () => {
  loading.value = true
  loadError.value = ''
  try {
    const res = await projectStore.loadToolsConfig()
    catalog.value = res?.catalog || []
    working.value = res?.config ? JSON.parse(JSON.stringify(res.config)) : {}
    // Struttura completa PRIMA del render: i v-model hanno dove scrivere e il confronto per
    // "modifiche non salvate" non scatta per chiavi create al volo.
    for (const tool of catalog.value) for (const call of tool.calls || []) {
      outcomeCfg(tool.id, call.id)
      if (call.file) fileCfg(tool.id, call.id)
    }
    originalJson.value = JSON.stringify(working.value)
  } catch (e) {
    loadError.value = `${t('admConfig.tools.loadError')}: ${e?.message || e}`
  } finally {
    loading.value = false
  }
}

const reset = () => {
  working.value = JSON.parse(originalJson.value || '{}')
  saveError.value = ''
  saveErrors.value = []
  saveSuccess.value = ''
  warnings.value = []
}

// Nel file finisce solo ciò che è configurato: niente chiavi vuote
const cleanPayload = () => {
  const out = {}
  for (const [toolId, tool] of Object.entries(working.value || {})) {
    const calls = {}
    for (const [callId, call] of Object.entries(tool?.calls || {})) {
      const entry = {}
      if (call.enabled === false) entry.enabled = false
      const inputs = Object.fromEntries(Object.entries(call.inputs || {}).filter(([, v]) => v))
      const outputs = Object.fromEntries(Object.entries(call.outputs || {}).filter(([, v]) => v))
      if (Object.keys(inputs).length) entry.inputs = inputs
      if (Object.keys(outputs).length) entry.outputs = outputs
      const o = call.outcome || {}
      const outcome = {}
      for (const k of ['source', 'statusField', 'checkboxField', 'successCategory', 'errorCategory', 'unknownCategory']) {
        if (o[k]) outcome[k] = o[k]
      }
      const successValues = (o.successValues || []).map(v => (v && typeof v === 'object' ? v.value ?? v.title : v)).filter(Boolean)
      if (successValues.length) outcome.successValues = successValues
      const categories = Object.fromEntries(Object.entries(o.categories || {}).filter(([, v]) => Array.isArray(v) && v.length))
      if (Object.keys(categories).length) outcome.categories = categories
      if (Object.keys(outcome).length) entry.outcome = outcome
      const file = Object.fromEntries(
        ['folder', 'nameTemplate', 'footer'].map(k => [k, (call.file?.[k] || '').trim()]).filter(([, v]) => v))
      if (Object.keys(file).length) entry.file = file
      calls[callId] = entry
    }
    out[toolId] = { calls }
  }
  return out
}

const save = async () => {
  saving.value = true
  saveError.value = ''
  saveErrors.value = []
  saveSuccess.value = ''
  warnings.value = []
  try {
    const payload = cleanPayload()
    const result = await projectStore.saveTools(payload)
    warnings.value = result?.warnings || []
    saveSuccess.value = t('admConfig.tools.saved', { backup: result?.backup || '—' })
    await load()
  } catch (e) {
    saveError.value = e?.response?.data?.message || e?.message || t('admConfig.tools.saveError')
    saveErrors.value = e?.response?.data?.errors || []
    warnings.value = e?.response?.data?.warnings || []
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>
